import cx from 'classnames';
import { useGetApprovalItemParticipations } from 'pages/approvals/hooks/useGetApprovalItemParticipations';
import { useGetApprovalTeams } from 'pages/approvals/hooks/useGetApprovalTeams';
import { ParticipationRow } from 'pages/approvals/views/ParticipationRow';
import { CircleFill, QuestionCircleFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

/**
 * @param {import('models/approval.models').ApprovalItem} approvalItem
 */
export function ApprovalItemTeams({ approvalItem }) {
    const { t } = useTranslation();
    const teams = useGetApprovalTeams();

    return (
        <>
            {approvalItem.teamRules.map((teamRule, index) => {
                const team = teams.find((team) => team['@id'] === teamRule.team);

                if (!team) {
                    return null;
                }

                return <RenderTeam team={team} teamRule={teamRule} approvalItem={approvalItem} key={`team-${index}`} />;
            })}
        </>
    );
}

/**
 * @param {import('models/approval.models').ApprovalTeam} team
 * @param {import('models/approval.models').ApprovalTeamRule} teamRule
 * @param {import('models/approval.models').ApprovalItem} approvalItem
 */
function RenderTeam({ team, teamRule, approvalItem }) {
    const { t } = useTranslation();
    const { items } = useGetApprovalItemParticipations(approvalItem['@id']);
    const teamUsers = items.filter((participant) => participant.team === team['@id']);

    if (teamUsers.length === 0) {
        return null;
    }

    const numberOfCompleted = teamUsers.filter(
        (participation) => participation.action === 'completed' || participation.action === 'approved',
    ).length;

    const tooltip = renderTeamStrategy(teamRule);

    return (
        <div className="mb-3">
            <div
                className="flex-center font-weight-bold text-uppercase pb-2 mt-4"
                style={{
                    borderBottom: '1px solid #cbd5e1',
                    fontSize: 12,
                    letterSpacing: 0,
                    color: '#475569',
                }}
            >
                {team.teamType === 'approval' && (
                    <CircleFill
                        size={8}
                        className={cx('mr-2', {
                            'text-success': teamRule.teamStatus === 'satisfied',
                            'text-warning': teamRule.teamStatus === 'pending',
                        })}
                    />
                )}
                {team.name} ({numberOfCompleted}/{teamUsers.length})
                {tooltip && (
                    <QuestionCircleFill size={14} className="ml-2 text-muted cursor-help" data-uk-tooltip={tooltip} />
                )}
            </div>

            {teamUsers.map((item) => (
                <ParticipationRow approvalItem={approvalItem} participation={item} key={`participation-${item.id}`} />
            ))}
        </div>
    );

    /**
     * @param {import('models/approval.models').ApprovalTeamRule} teamRule
     */
    function renderTeamStrategy(teamRule) {
        switch (teamRule.strategy) {
            case 'one_must_approve':
                return t('ai:approvalItemTeams.strategy.oneMustApprove');
            case 'all_must_approve':
                return t('ai:approvalItemTeams.strategy.allMustApprove');
        }
    }
}
